import { feedbacks, feedbackTagMappings, feedbackTags } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// Base schemas
export const selectFeedbackSchema = createSelectSchema(feedbacks)
export const selectFeedbackTagSchema = createSelectSchema(feedbackTags)
export const selectFeedbackTagMappingSchema =
  createSelectSchema(feedbackTagMappings)

// Insert schema for feedback (without auto-generated fields)
export const insertFeedbackSchema = createInsertSchema(feedbacks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdByUserId: true, // Will be set from context
})

// Create feedback schema with tags
export const createFeedbackSchema = z.object({
  visitId: z.number(),
  ratingValue: z.number().min(1).max(5),
  description: z.string().optional().nullable(),
  tagIds: z
    .array(
      z.object({
        feedbackTagId: z.number(),
      }),
    )
    .optional(),
})

// Update feedback schema
export const updateFeedbackSchema = z.object({
  ratingValue: z.number().min(1).max(5).optional(),
  description: z.string().optional().nullable(),
  tagIds: z
    .array(
      z.object({
        feedbackTagId: z.number(),
      }),
    )
    .optional(),
})

// Feedback with relations
export const selectFeedbackWithRelationsSchema = selectFeedbackSchema.extend({
  visit: z
    .object({
      id: z.number(),
      title: z.string().nullable(),
      startTime: z.string(),
      endTime: z.string(),
      status: z.enum(['scheduled', 'in_progress', 'completed', 'cancelled']),
    })
    .optional(),
  createdByUser: z
    .object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .optional(),
  feedbackTagMappings: z
    .array(
      selectFeedbackTagMappingSchema.extend({
        feedbackTag: selectFeedbackTagSchema.optional(),
      }),
    )
    .optional(),
})

// Type exports
export type Feedback = z.infer<typeof selectFeedbackSchema>
export type FeedbackTag = z.infer<typeof selectFeedbackTagSchema>
export type FeedbackTagMapping = z.infer<typeof selectFeedbackTagMappingSchema>
export type FeedbackWithRelations = z.infer<
  typeof selectFeedbackWithRelationsSchema
>
export type CreateFeedback = z.infer<typeof createFeedbackSchema>
export type UpdateFeedback = z.infer<typeof updateFeedbackSchema>
